import {createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useMemo, useState} from "react";
import {
  QuestionCategoriesQuery,
  QuestionsQuery,  TagsQuery,
  useQuestionCategoriesQuery,
  useQuestionsQuery, useTagsQuery
} from "@generated/graphql";
import {Category} from "pages/Faq/types";
import {debounce} from "lodash";
import {useSearchParams} from "react-router-dom";

const archivedCategoryTitle = 'Архив мероприятий';

interface QuestionContextProps {
  loading: boolean;
  loadingCategories: boolean;
  loadingQuestions: boolean;
  loadingTags: boolean;
  questionList: QuestionsQuery['questions']['data'];
  categoryList: QuestionCategoriesQuery['questionCategories']['data'];
  tagList: TagsQuery['tags']['data'];
  selectedCategory: Category | null;
  selectedSubCategory: string | null;
  selectedTags: string[];
  setSelectedCategory: Dispatch<SetStateAction<Category | null>>
  setSelectedSubCategory: Dispatch<SetStateAction<string | null>>;
  setSelectedTags: Dispatch<SetStateAction<string[]>>;
  searchInput: string;
  setSearchInput: Dispatch<SetStateAction<string>>;
  archivedChecked: boolean;
  setArchivedChecked: Dispatch<SetStateAction<boolean>>;
  isComeFromLink: boolean;
  setIsComeFromLink: Dispatch<SetStateAction<boolean>>;
  openedByLinkQuestion: null | string;
  setOpenedByLinkQuestion: Dispatch<SetStateAction<string | null>>;
}

const QuestionListContext = createContext<QuestionContextProps>({
  loading: false,
  loadingCategories: false,
  loadingTags: false,
  tagList: [],
  categoryList: [],
  loadingQuestions: false,
  questionList: [],
  selectedCategory: null,
  selectedSubCategory: null,
  archivedChecked: false,
  isComeFromLink: false,
  openedByLinkQuestion: null,
  selectedTags: [],
  setIsComeFromLink: () => {},
  setOpenedByLinkQuestion: () => {},
  setArchivedChecked: () => {},
  setSelectedCategory: () => {
  },
  setSelectedSubCategory: () => {

  },
  setSelectedTags: () => {
  },
  searchInput: '',
  setSearchInput: () => {
  }
});

const QuestionListProvider = ({children}: { children: ReactNode }) => {
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState<string>("");
  const [debouncedSearchInput, setDebouncedSearchInput] = useState<string>("");
  const [archivedChecked, setArchivedChecked] = useState<boolean>(false);
  const [isComeFromLink, setIsComeFromLink] = useState<boolean>(false);
  const [openedByLinkQuestion, setOpenedByLinkQuestion] = useState<string | null>(null);

  const [searchParams] = useSearchParams();

  const id = searchParams.get('id');
  const category = searchParams.get('category');
  const subcategory = searchParams.get('subcategory');

  const {data: categories, loading: loadingCategories} = useQuestionCategoriesQuery()

  const categoryList = categories?.questionCategories.data;

  const archivedCategorySelected = selectedCategory?.title === archivedCategoryTitle;

  const {data: tags, loading: loadingTags} = useTagsQuery({
    variables: {
      filter: archivedCategorySelected || archivedChecked? { isArchived: true }: {
        questionCategoryId: selectedCategory?.id,
        questionSubCategoryId: selectedSubCategory
      }
    }
  })

  const tagList = tags?.tags.data;

  useEffect(() => {
    if(id && !isComeFromLink && !loadingCategories){
      setIsComeFromLink(true);
      setOpenedByLinkQuestion(id);
      if (category) {
        const selectedCategory = categoryList?.find(c => c.id === category);
        setSelectedCategory(selectedCategory ?? null);
        if (subcategory) {
          setSelectedSubCategory(subcategory);
        }
      }
    }
  }, [id, isComeFromLink, category, subcategory, categoryList, loadingCategories]);

  const {data: questions, loading: loadingQuestions} = useQuestionsQuery({
    variables: {
      filter: {
        questionCategoriesIds: selectedCategory && !archivedCategorySelected ? [selectedCategory.id] : undefined,
        questionSubCategoriesIds: selectedSubCategory && !archivedCategorySelected ? [selectedSubCategory] : undefined,
        tagsIds: selectedTags.length ? selectedTags : undefined,
        search: debouncedSearchInput || undefined,
        isArchived: archivedChecked || archivedCategorySelected,
      },
      pagination: {
        limit: 100,
        page: 0
      }
    }
  });

  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedSearchInput(searchInput);
    }, 300);

    handler();

    return () => {
      handler.cancel();
    };
  }, [searchInput]);


  useEffect(() => {
    setSelectedTags([])
  }, [selectedCategory?.id])

  const questionList = questions?.questions.data;

  const loading = loadingCategories || loadingQuestions;

  const value = useMemo(() => ({
    categoryList,
    loadingCategories,
    loading,
    questionList,
    loadingQuestions,
    selectedCategory,
    selectedTags,
    archivedChecked,
    setArchivedChecked,
    setSelectedTags,
    setSelectedCategory,
    tagList,
    loadingTags,
    setSelectedSubCategory,
    selectedSubCategory,
    searchInput,
    isComeFromLink,
    setOpenedByLinkQuestion,
    openedByLinkQuestion,
    setIsComeFromLink,
    setSearchInput,
  }), [categoryList, loadingCategories, loading, questionList, loadingQuestions,
    selectedCategory,
    setOpenedByLinkQuestion,
    openedByLinkQuestion,
    selectedTags,
    setArchivedChecked,
    archivedChecked,
    isComeFromLink,
    setIsComeFromLink,
    setSelectedTags,
    setSelectedCategory,
    selectedSubCategory,
    setSelectedSubCategory,
    tagList,
    loadingTags,
    searchInput,
    setSearchInput]);

  return (
    <QuestionListContext.Provider value={value}>
      {children}
    </QuestionListContext.Provider>
  )
}

const useQuestionListContext = (): QuestionContextProps => {
  const context = useContext(QuestionListContext);
  if (context === undefined) {
    throw new Error('useQuestionListContext must be used within a QuestionListProvider');
  }
  return context;
};

export {QuestionListProvider, useQuestionListContext};
